<script setup lang="ts">
import { computed } from 'vue';
import { useDashboardStats } from '@/composables/useDashboardStats';
import { useI18n } from 'vue-i18n';

const { totalSocietes, totalCommerces, alertCount, taskCount, isPending } =
  useDashboardStats();
const { t } = useI18n({ useScope: 'global' });

const stats = computed(() => [
  {
    key: 'societes',
    label: t('dashboard.kpis.societes'),
    value: isPending.value ? '…' : String(totalSocietes.value),
    sub: isPending.value
      ? ''
      : t('dashboard.kpis.societesSub', { n: totalSocietes.value }),
    icon: 'pi-building',
    alert: false,
  },
  {
    key: 'commerces',
    label: t('dashboard.kpis.commerces'),
    value: isPending.value ? '…' : String(totalCommerces.value),
    sub: isPending.value
      ? ''
      : t('dashboard.kpis.commercesSub', { n: totalCommerces.value }),
    icon: 'pi-shop',
    alert: false,
  },
  {
    key: 'alertes',
    label: t('dashboard.kpis.alertes'),
    value: isPending.value ? '…' : String(alertCount.value),
    sub:
      alertCount.value === 0
        ? t('dashboard.kpis.alertesNone')
        : t('dashboard.kpis.alertesSub', { n: alertCount.value }),
    icon: 'pi-exclamation-circle',
    alert: alertCount.value > 0,
  },
  {
    key: 'tickets',
    label: t('dashboard.kpis.tickets'),
    value: '0',
    sub: t('dashboard.kpis.ticketsNone'),
    icon: 'pi-ticket',
    alert: false,
  },
  {
    key: 'taches',
    label: t('dashboard.kpis.taches'),
    value: isPending.value ? '…' : String(taskCount.value),
    sub:
      taskCount.value === 0
        ? t('dashboard.kpis.tachesOk')
        : t('dashboard.kpis.tachesSub', { n: taskCount.value }),
    icon: 'pi-chart-bar',
    alert: false,
  },
]);
</script>

<template>
  <section class="stats">
    <div
      v-for="s in stats"
      :key="s.key"
      class="stat"
      :class="{ 'stat--alert': s.alert }"
    >
      <div class="stat__top">
        <span class="stat__label" :class="{ 'stat__label--alert': s.alert }">{{
          s.label
        }}</span>
        <i
          :class="`pi ${s.icon} stat__icon`"
          :style="s.alert ? 'color: rgba(239,68,68,0.35)' : ''"
        />
      </div>
      <div class="stat__bottom">
        <span
          class="stat__value"
          :class="{
            'stat__value--alert': s.alert,
            'stat__value--loading': isPending,
          }"
        >
          {{ s.value }}
        </span>
        <p class="stat__sub" :class="{ 'stat__sub--alert': s.alert }">
          {{ s.sub }}
        </p>
      </div>
    </div>
  </section>
</template>

<style scoped>
.stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}
@media (min-width: 480px) {
  .stats {
    grid-template-columns: repeat(3, 1fr);
  }
}
@media (min-width: 900px) {
  .stats {
    grid-template-columns: repeat(5, 1fr);
  }
}

.stat {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;
  transition: border-color 0.2s;
  cursor: default;
}
.stat:hover {
  border-color: #ffd60a;
}
.stat--alert:hover {
  border-color: rgba(239, 68, 68, 0.4);
}

.stat__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.stat__label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(11, 19, 32, 0.4);
}
.stat__label--alert {
  color: #f87171;
}

.stat__icon {
  font-size: 0.875rem;
  color: rgba(11, 19, 32, 0.2);
  flex-shrink: 0;
}

.stat__bottom {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.stat__value {
  display: block;
  font-size: 1.5rem;
  font-weight: 800;
  color: #0b1320;
  letter-spacing: -0.02em;
  line-height: 1;
}
.stat__value--alert {
  color: #ef4444;
}
.stat__value--loading {
  opacity: 0.2;
}

.stat__sub {
  margin: 0;
  font-size: 0.625rem;
  font-weight: 700;
  color: rgba(11, 19, 32, 0.4);
}
.stat__sub--alert {
  color: #f87171;
}
</style>

<style>
[data-theme='dark'] .stat {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .stat:hover {
  border-color: #ffd60a;
}
[data-theme='dark'] .stat__label {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .stat__icon {
  color: rgba(255, 255, 255, 0.15);
}
[data-theme='dark'] .stat__value {
  color: #f1f5f9;
}
[data-theme='dark'] .stat__sub {
  color: rgba(255, 255, 255, 0.3);
}
</style>
